import {
  type ComputedRef,
  type MaybeRefOrGetter,
  computed,
  defineAsyncComponent,
  toValue,
  watch,
} from 'vue'
import { ApolloCache, type Reference } from '@apollo/client/cache'
import { useNewProjectModal } from '/@/common/projects/modals/NewProjectModal'
import { useLoading } from '/@/core/elements/loading'
import { useTracker } from '/@/core/tracking/api'
import { useDesignerApi } from '/@/data/designer/api'
import { useCurrentDesigner } from '/@/data/designer/current'
import {
  useProjectApi,
  useProjectData,
  useProjectsListExists,
} from '/@/common/projects/api/index'
import {
  projectAccessFragment,
  projectQuery,
} from '/@/common/projects/api/fragments'
import type {
  Project,
  ProjectAccess,
  ProjectInfo,
  ProjectStatus,
  ProjectType,
} from './types'
import { isProduct, isProjectGenericThing } from '/@/common/things/utils'
import { usePermission } from '/@/common/tiers'
import { stellaModal } from '/@/stella/StellaModal'
import type { ProjectThingType } from '/@/common/things/types'
import type { ViewerType } from '/@/utils/misc'
import { useQuery } from '@meesayen/apollo-composable'
import { gql } from '@apollo/client/core'
import { projectBudgetFragment } from '../budget/fragments'
import type { ProjectBudget } from '../budget/types'

export type ProjectGuardTargetType =
  | 'Vizi'
  | 'Thing'
  | 'Color Pal'
  | 'Design Challenge'
  | 'Daily Play'
  | 'Cart'

export interface ProjectGuardVerify {
  project: ProjectInfo
  isNew: boolean
}

export interface ProjectVerifyOptions {
  strict?: boolean
  automaticallyCreate?: boolean
}

const AUTOMATIC_PROJECT_NAME = 'Playing Around'
const AUTOMATIC_CART_PROJECT_NAME = 'Cart'
const DEFAULT_PROJECT_NAME = 'Untitled Project'

export function useProjectGuard() {
  const newProjectModal = useNewProjectModal()
  const designerApi = useDesignerApi()
  const projectApi = useProjectApi()
  const loadingApi = useLoading()
  const tracker = useTracker()
  const createProjectPermission = usePermission('CREATE_PROJECTS')

  const { designer, loading: designerLoading } = useCurrentDesigner()
  const { hasProjects, loading: projectsLoading } = useProjectsListExists(
    computed(() => ({})),
  )
  const automaticProject = computed(() => designer.value?.automaticProject)
  const automaticCartProject = computed(
    () => designer.value?.automaticCartProject,
  )
  const loading = computed(
    () =>
      designerLoading.value ||
      projectsLoading.value ||
      createProjectPermission.loading.value,
  )

  async function switchProject(id: string) {
    await loadingApi.waitTask(() => {
      if (!designer.value) return

      tracker.send('Project Selection Confirmed', {
        sameProject: designer.value.currentProject?.id === id,
      })

      return designerApi.updateCurrentProject(id)
    })
  }

  function verifyImpl(
    resolve: (
      value: ProjectGuardVerify | PromiseLike<ProjectGuardVerify>,
    ) => void,
    reject: (reason?: any) => void,
    targetName?: ProjectGuardTargetType,
    options?: ProjectVerifyOptions,
  ) {
    if (targetName === 'Daily Play' || targetName === 'Design Challenge') {
      if (automaticProject.value) {
        resolve({
          project: automaticProject.value,
          isNew: false,
        })
      } else {
        loadingApi.waitTask(
          async () =>
            await projectApi
              .create({
                name: AUTOMATIC_PROJECT_NAME,
                visibility: 'PRIVATE',
                projectType: 'AUTOMATIC_DAILY_PLAY_DESIGN_CHALLENGE',
              })
              .then((project: Project | undefined) => {
                if (project)
                  resolve({
                    project,
                    isNew: true,
                  })
                else reject('Failed to create project')
              }),
        )
      }
    } else if (targetName === 'Cart') {
      if (automaticCartProject.value) {
        resolve({
          project: automaticCartProject.value,
          isNew: false,
        })
      } else {
        loadingApi.waitTask(
          async () =>
            await projectApi
              .create({
                name: AUTOMATIC_CART_PROJECT_NAME,
                visibility: 'PRIVATE',
                projectType: 'AUTOMATIC_SHOPPING_CART',
              })
              .then((project: Project | undefined) => {
                if (project)
                  resolve({
                    project,
                    isNew: true,
                  })
                else reject('Failed to create project')
              }),
        )
      }
    } else {
      const currentProject = designer.value?.currentProject?.access?.canEdit
        ? designer.value?.currentProject
        : null
      if (currentProject) {
        resolve({
          project: currentProject,
          isNew: false,
        })
      } else {
        tracker.send('Project Selection Shown')

        if (
          !createProjectPermission.hasPermission.value &&
          hasProjects.value &&
          (targetName === 'Vizi' || targetName === 'Color Pal')
        ) {
          stellaModal.open({
            content: defineAsyncComponent(
              () =>
                import(
                  '/@/common/projects/modals/SelectProjectModal/SelectProjectModalContent.vue'
                ),
            ),
            data: {
              onSubmit: async (project: ProjectInfo) => {
                await switchProject(project.id)
                resolve({
                  project,
                  isNew: false,
                })
              },
            },
          })
        } else if (
          createProjectPermission.hasPermission.value &&
          options?.automaticallyCreate
        ) {
          loadingApi.waitTask(async () => {
            await projectApi
              .create({
                name: DEFAULT_PROJECT_NAME,
                visibility: 'PUBLIC',
              })
              .then((project: Project | undefined) => {
                if (project)
                  resolve({
                    project,
                    isNew: true,
                  })
                else reject('Failed to create project')
              })
          })
        } else {
          createProjectPermission
            .tierGuard(
              () =>
                newProjectModal.show({
                  context: targetName,
                  onSubmit: async (project, isNew) => {
                    await switchProject(project.id)
                    resolve({
                      project,
                      isNew,
                    })
                  },
                  onCancel: reject,
                  strict: options?.strict ?? false,
                }),
              {
                source: 'Project Guard',
              },
            )
            .catch(reject)
        }
      }
    }
  }

  async function verify(
    targetName?: ProjectGuardTargetType,
    options?: ProjectVerifyOptions,
  ): Promise<ProjectGuardVerify> {
    // Cannot use `unwatch` if `watch` is called with `immediate: true`, so check values first before using `watch`
    if (!loading.value) {
      return new Promise((resolve, reject) => {
        verifyImpl(resolve, reject, targetName, options)
      })
    } else {
      return new Promise((resolve, reject) => {
        const unwatch = watch(
          loading,
          l => {
            if (l) return
            verifyImpl(resolve, reject, targetName, options)
            unwatch()
          },
          {
            once: true,
          },
        )
      })
    }
  }

  return {
    loading,
    verify,
  }
}

export function useProjectCache() {
  function getCachedProject(projectId: ComputedRef<string>) {
    return useProjectData(projectId, 'cache-only')
  }

  function getApolloCachedProject(
    cache: ApolloCache<unknown>,
    projectId: string,
  ) {
    return cache.readQuery<{ project: Project }>({
      query: projectQuery,
      variables: {
        projectId,
      },
    })?.project
  }

  function addProjectBudget(
    cache: ApolloCache<unknown>,
    budget: Reference,
    projectId: string,
  ) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const id = cache.identify({ ...cachedProject })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return budget
        },
      },
    })
  }

  function addSectionBudget(
    cache: ApolloCache<unknown>,
    budget: Reference,
    projectId: string,
    sectionId: string,
  ) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const cachedSection = cachedProject.sections.find(s => s.id === sectionId)
    if (!cachedSection) return
    const id = cache.identify({ ...cachedSection })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return budget
        },
      },
    })
  }

  function deleteProjectBudget(cache: ApolloCache<unknown>, projectId: string) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const id = cache.identify({ ...cachedProject })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return null
        },
      },
    })
  }

  function deleteSectionBudget(
    cache: ApolloCache<unknown>,
    projectId: string,
    sectionId: string,
  ) {
    const cachedProject = getApolloCachedProject(cache, projectId)
    if (!cachedProject) return
    const cachedSection = cachedProject.sections.find(s => s.id === sectionId)
    if (!cachedSection) return
    const id = cache.identify({ ...cachedSection })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['budget']() {
          return null
        },
      },
    })
  }

  return {
    getCachedProject,
    addProjectBudget,
    deleteProjectBudget,
    addSectionBudget,
    deleteSectionBudget,
  }
}

export function useProjectThingCache() {
  function getCachedProjectThing(
    cache: ApolloCache<unknown>,
    projectId: string,
    projectThingId: string,
    projectThingType: ProjectThingType,
  ) {
    const project = cache.readQuery<{ project: Project }>({
      query: projectQuery,
      variables: {
        projectId,
      },
    })?.project
    const projectThing = project?.things.find(t => {
      return t.id === projectThingId && t.thingType === projectThingType
    })
    return projectThing &&
      (isProduct(projectThing) || isProjectGenericThing(projectThing))
      ? projectThing
      : null
  }

  function addProjectThingBudget(
    cache: ApolloCache<unknown>,
    budget: Reference,
    projectId: string,
    projectThingId: string,
    projectThingType: ProjectThingType,
  ) {
    const cachedProjectThing = getCachedProjectThing(
      cache,
      projectId,
      projectThingId,
      projectThingType,
    )
    if (!cachedProjectThing) return
    const id = cache.identify({ ...cachedProjectThing })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['thingBudget']() {
          return budget
        },
      },
    })
  }

  return {
    addProjectThingBudget,
  }
}

export function useProjectAccessData(
  projectId: MaybeRefOrGetter<string | null | undefined>,
) {
  const { result } = useQuery<{
    project: {
      status: ProjectStatus
      projectType: ProjectType
      access: ProjectAccess
    }
  }>(
    gql`
      ${projectAccessFragment}
      query getProjectAccessData($projectId: String!) {
        project(id: $projectId) {
          id
          status
          projectType
          access {
            ...ProjectAccessProps
          }
        }
      }
    `,
    () => ({
      projectId: toValue(projectId),
    }),
    () => ({
      enabled: Boolean(toValue(projectId)),
    }),
  )
  return computed(() =>
    result.value
      ? {
          ...result.value.project.access,
          projectStatus: result.value.project.status,
          projectType: result.value.project.projectType,
        }
      : null,
  )
}

export function useProjectViewer(
  projectId: MaybeRefOrGetter<string | null | undefined>,
) {
  const { designer } = useCurrentDesigner()
  const projectAccess = useProjectAccessData(projectId)
  return computed<ViewerType>(() => {
    const pa = projectAccess.value
    if (!pa) return 'anonymous'
    const role = pa.role
    if (role === 'OWNER') return 'owner'
    if (role === 'COLLABORATOR') return 'collaborator'
    if (role === 'CLIENT') return 'client'
    if (designer.value?.id) return 'designer'
    return 'anonymous'
  })
}

export function useProjectBudgetData(
  projectId: MaybeRefOrGetter<string | null | undefined>,
) {
  const { result } = useQuery<{ project: { budget: ProjectBudget } }>(
    gql`
      ${projectBudgetFragment}
      query getProjectBudgetData($projectId: String!) {
        project(id: $projectId) {
          id
          budget {
            ...ProjectBudgetProps
          }
        }
      }
    `,
    () => ({
      projectId: toValue(projectId),
    }),
    () => ({
      enabled: Boolean(toValue(projectId)),
    }),
  )
  return computed(() => result.value?.project.budget)
}

export const projectLibraryIds = [
  '548542',
  '548543',
  '548203',
  '548597',
  '548598',
  '603354',
]
